import { ButtonSizes, FlowbiteButtonInnerTheme } from 'flowbite-react';
import { DeepPartial } from 'flowbite-react/lib/esm/types';

export interface ButtonColorsProps {
    [key: string]: string | undefined;
    primary: string;
    secondary?: string;
    outline?: string;
    destruct?: string;
    ghost?: string;
}

export type ButtonColorsEnum = keyof ButtonColorsProps;

export interface ButtonSizesProps extends ButtonSizes {
    [key: string]: string;
    md: string;
}

export type ButtonSizesEnum = keyof ButtonSizesProps;

export interface ButtonThemeProps {
    base: string;
    color: ButtonColorsProps;
    size: ButtonSizesProps;
    inner?: DeepPartial<FlowbiteButtonInnerTheme>;
}

export const buttonTheme: ButtonThemeProps = {
    // All button variant are designed not to have focus ring. Focus ring is added here to ensure expected style when other components host buttons
    base: 'justify-center items-center inline-flex disabled:opacity-50 truncate !focus:ring-0 !ring-0 p-0',
    color: {
        primary:
            'bg-system-secondary border-system-on-secondary text-system-on-secondary hover:primary-hover-state active:primary-active-state primary-select-state',
        secondary:
            'bg-system-primary text-system-on-primary hover:secondary-hover-state active:secondary-active-state dark:hover:primary-hover-state dark:active:primary-active-state primary-select-state',
        outline:
            'bg-system-surface border-2 border-solid border-system-outline text-system-on-surface hover:primary-hover-state active:primary-active-state primary-select-state',
        destruct:
            'bg-system-error text-system-on-error hover:secondary-hover-state active:secondary-active-state primary-select-state',
        ghost: 'bg-transparent text-system-on-surface hover:primary-hover-state active:primary-active-state primary-select-state',
    },
    size: {
        xs: 'px-3 py-2 items-center gap-2',
        sm: 'px-3 py-2 items-center gap-2',
        md: 'px-5 py-2.5 items-center gap-2',
        lg: 'px-5 py-3 items-center gap-2',
        xl: 'px-6 py-3.5 items-center gap-2',
    },
};

export const buttonIconTheme: ButtonThemeProps = {
    ...buttonTheme,
    base: 'justify-center items-center inline-flex relative',
    size: {
        xs: 'p-1',
        sm: 'p-2',
        md: 'p-2.5',
        lg: 'p-3',
        xl: 'p-3.5',
    },
};

/** Keyboard focus ring shared by the select triggers, so a borderless one is as visible as the field. */
export const selectTriggerFocusRing =
    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-system-primary';

/** Portal-select trigger, styled as an Input on the surface ground so it matches inputs in the same row. */
export const selectFieldButtonTheme: ButtonThemeProps = {
    base: `flex items-center justify-between w-full max-w-[384px] disabled:opacity-50 truncate ${selectTriggerFocusRing}`,
    inner: {
        base: 'flex w-full',
    },
    color: {
        primary:
            'rounded-lg border border-solid bg-system-surface border-system-outline-variant text-system-on-surface' +
            ' hover:primary-hover-state active:primary-active-state',
    },
    size: {
        xs: 'p-2 items-center gap-2',
        sm: 'p-2 items-center gap-2',
        md: 'p-2.5 items-center gap-2',
        lg: 'p-4 items-center gap-2',
        xl: 'p-4 items-center gap-2',
    },
};

/**
 * Borderless portal-select trigger, for a select inside a table cell that should read as the cell's text.
 * The border is transparent so an error can recolour it without a shift. Padding is tighter than the field
 * theme's, less that 1px border; the size keys mirror the field theme's.
 */
export const selectFieldGhostButtonTheme: ButtonThemeProps = {
    ...selectFieldButtonTheme,
    color: {
        primary:
            'rounded-lg border border-solid border-transparent bg-transparent text-system-on-surface' +
            ' hover:primary-hover-state active:primary-active-state',
    },
    size: {
        xs: 'px-[7px] py-[3px] items-center gap-2',
        sm: 'px-[7px] py-[5px] items-center gap-2',
        md: 'p-[7px] items-center gap-2',
        lg: 'p-[11px] items-center gap-2',
        xl: 'p-[11px] items-center gap-2',
    },
};

/**
 * Ghost trigger placeholder. On-surface-variant falls to about 3.4:1 on a hovered table row; on-surface at 65%
 * keeps at least 5:1 on resting, hovered, pressed and selected rows in both themes, and still reads lighter than a value.
 */
export const selectGhostPlaceholderClass = 'text-[color:color-mix(in_srgb,var(--sys-on-surface)_65%,transparent)]';

export const selectButtonTheme: ButtonThemeProps = {
    base: 'flex items-center justify-between w-full max-w-[384px] disabled:opacity-50 truncate',
    inner: {
        base: 'flex w-full',
    },
    color: {
        primary:
            'rounded-lg border border-system-outline-variant border-solid bg-system-surface-container hover:primary-hover-state active:primary-active-state',
        secondary:
            'bg-system-primary text-system-on-primary hover:secondary-hover-state active:secondary-active-state dark:hover:primary-hover-state dark:active:primary-active-state',
        outline:
            'border-2 border-system-outline border-solid text-system-on-secondary hover:primary-hover-state active:primary-active-state',
        destruct: 'bg-system-error text-system-on-primary hover:secondary-hover-state active:secondary-active-state',
        // the base adds no focus style, so the borderless trigger brings the field's ring
        ghost: `rounded-lg bg-transparent hover:primary-hover-state active:primary-active-state ${selectTriggerFocusRing}`,
    },
    size: {
        xs: 'px-1 py-1 items-center gap-2',
        sm: 'px-2 py-2 items-center gap-2',
        md: 'px-3 py-3 items-center gap-2',
        lg: 'px-4 py-4 items-center gap-2',
        xl: 'px-5 py-5 items-center gap-2',
    },
};
